import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../widgets";
import strings from "../../../utils/strings";
import Icons from "../../../Assets/Icons";
import "./TransportationList.css";
import { fetchTransportationFee } from "../../../modules/NursignCare/TransportationFee/transportationFeeThunk";

const formatVnd = (value) => {
  const num =
    typeof value === "number"
      ? value
      : Number(String(value || "").replace(/,/g, ""));
  if (!Number.isFinite(num)) return "";
  return `₫${num.toLocaleString("en-US")}`;
};

const TransportationList = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { fee } = useSelector((state) => state.transportationFee);

  const breadcrumbs = ["nursingCare", "transportationFees"];
  const paths = [strings.navigation.manageNurseReq, location.pathname];

  const [openZoneId, setOpenZoneId] = useState("urban");
  const [selectedModeByZone, setSelectedModeByZone] = useState({
    urban: "taxi",
    rural: "taxi",
  });

  useEffect(() => {
    dispatch(fetchTransportationFee());
  }, [dispatch]);

  const safeFee = useMemo(() => {
    if (fee) return fee;
    return {
      firstKm: 0,
      transportFees: {
        motorbike: { workingHours: {}, nonWorkingHours: {} },
        taxi: { workingHours: {}, nonWorkingHours: {} },
      },
    };
  }, [fee]);

  const onToggleZone = (zoneId) => {
    setOpenZoneId((prev) => (prev === zoneId ? "" : zoneId));
  };

  const onModeChange = (zoneId, mode) => {
    setSelectedModeByZone((prev) => ({
      ...prev,
      [zoneId]: mode,
    }));
  };

  const onEditZone = (type) => {
    nav(strings.navigation.editZoneFees, {
      state: { type },
    });
  };

  // 🔥 SAME MAPPING AS API (NO CHANGE NEEDED LATER)
  const getFees = (mode, type) => {
    const data = safeFee?.transportFees?.[mode];

    const getVal = (section, opd) =>
      data?.[section]?.[opd]?.[type] ?? 0;

    return {
      working: {
        intl: getVal("workingHours", "opdInternationalInsured"),
        local: getVal("workingHours", "opdLocalInsured"),
        subsidized: getVal("workingHours", "opdSubsidized"),
      },
      nonWorking: {
        intl: getVal("nonWorkingHours", "opdInternationalInsured"),
        local: getVal("nonWorkingHours", "opdLocalInsured"),
        subsidized: getVal("nonWorkingHours", "opdSubsidized"),
      },
    };
  };

  const zones = [
    {
      id: "urban",
      name: t("baseFareFees"),
      type: "baseFare",
    },
    {
      id: "rural",
      name: t("distancePerKmFees"),
      type: "perKmRate",
    },
  ];

  return (
    <div className="tf-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

      <div className="tf-heading">{t("transportationFeeList")}</div>

      <div className="tf-list-container">
        <div className="tf-zone-list">
          <div className="tf-list-header">
            <div className="tf-col-zone">{t("zoneName")}</div>
            <div className="tf-col-action">{t("action")}</div>
          </div>

          {zones.map((zone) => {
            const isOpen = openZoneId === zone.id;
            const selectedMode = selectedModeByZone[zone.id];
            const feeSet = getFees(selectedMode, zone.type);

            return (
              <div key={zone.id} className={`tf-zone ${isOpen ? "open" : ""}`}>
                <div
                  className="tf-zone-row"
                  onClick={() => onToggleZone(zone.id)}
                >
                  <div className="tf-zone-name">{zone.name}</div>

                  <div
                    className="tf-zone-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="tf-edit-btn"
                      onClick={() => onEditZone(zone.type)}
                    >
                      <img src={Icons.edit} alt="edit" />
                      <span>{t("edit")}</span>
                    </button>

                    <button className={`tf-expand-btn ${isOpen ? "open" : ""}`}
                    onClick={() => onToggleZone(zone.id)}>
                      
                      <img src={Icons.downArrow} alt="toggle" />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="tf-zone-expand">
                    <div className="tf-mode-row">
                      <label className="tf-radio">
                        <input
                          type="radio"
                          checked={selectedMode === "taxi"}
                          onChange={() => onModeChange(zone.id, "taxi")}
                        />
                        <span>{t("taxiFees")}</span>
                      </label>

                      <label className="tf-radio">
                        <input
                          type="radio"
                          checked={selectedMode === "motorbike"}
                          onChange={() => onModeChange(zone.id, "motorbike")}
                        />
                        <span>{t("motobikeFees")}</span>
                      </label>
                    </div>

                    <table className="tf-fee-table">
                      <thead>
                        <tr>
                          <th>{t("timeSlot")}</th>
                          <th>{t("opdInternationalInsured")}</th>
                          <th>{t("opdLocalInsured")}</th>
                          <th>{t("opdSubsidized")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{t("workingHours")}</td>
                          <td className="tf-blue">{formatVnd(feeSet.working.intl)}</td>
                          <td className="tf-green">{formatVnd(feeSet.working.local)}</td>
                          <td className="tf-purple">{formatVnd(feeSet.working.subsidized)}</td>
                        </tr>

                        <tr>
                          <td>{t("nonWorkingHours")}</td>
                          <td className="tf-blue">{formatVnd(feeSet.nonWorking.intl)}</td>
                          <td className="tf-green">{formatVnd(feeSet.nonWorking.local)}</td>
                          <td className="tf-purple">{formatVnd(feeSet.nonWorking.subsidized)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TransportationList;